import { useMemo } from "react";
import { formatNumber, formatPercent } from "./analyticsFormatters";
import {
  buildFunnelPeriodData,
  formatStepLabel,
} from "../utils/funnelVisualizationUtils";

const getBarColor = (conversion) => {
  if (conversion == null) return "bg-gray-400";
  if (conversion >= 70) return "bg-green-500";
  if (conversion >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

export default function FunnelChartCard({
  funnel,
  subtitle,
  pipelineStages = [],
}) {
  const steps = useMemo(() => {
    const periodData = buildFunnelPeriodData(funnel.steps, "thisWeek");
    const counts = periodData.map((row) => row.count);
    const topCount = counts[0] ?? 0;
    const maxCount = Math.max(topCount, ...counts, 1);

    return periodData.map((row) => ({
      label: formatStepLabel(row.label, row.key),
      users: row.count,
      pageViews: row.pageViews,
      uniquePageViews: row.uniquePageViews,
      conversion: row.conversionPercent,
      dropOff: row.dropOffPercent,
      progressPercent: Number(((row.count / maxCount) * 100).toFixed(1)),
    }));
  }, [funnel.steps]);

  const title = funnel.priceLabel
    ? `${funnel.label} (${funnel.priceLabel})`
    : funnel.label;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition overflow-hidden">
      <div className="border-b border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>

      <div className="p-6">
        <div className="space-y-2">
          {steps.map((step, index) => (
            <div key={step.label}>
              <div className="flex items-start gap-2">
                <span className="text-indigo-500 text-lg leading-none mt-0.5">○</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{step.label}</p>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden mt-2">
                    <div
                      className={`h-full rounded-full transition-all ${getBarColor(step.conversion)}`}
                      style={{ width: `${step.progressPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-1.5">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500">
                        {formatNumber(step.users)} Users
                      </p>
                      {(step.pageViews != null || step.uniquePageViews != null) && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {[
                            step.pageViews != null
                              ? `${formatNumber(step.pageViews)} page views`
                              : null,
                            step.uniquePageViews != null
                              ? `${formatNumber(step.uniquePageViews)} unique`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      {formatPercent(step.progressPercent)}
                    </span>
                  </div>
                </div>
              </div>

              {index < steps.length - 1 && (
                <div className="flex justify-center my-3">
                  <span className="text-sm text-red-500">
                    ↓ {formatPercent(steps[index + 1].dropOff ?? 0)} Drop-off
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">
            Detailed Breakdown
          </h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-3 font-medium">Step</th>
                  <th className="py-2 pr-3 font-medium">Users</th>
                  <th className="py-2 pr-3 font-medium">Conv %</th>
                  <th className="py-2 pr-3 font-medium">Drop-off</th>
                  <th className="py-2 font-medium">Progress</th>
                </tr>
              </thead>
              <tbody>
                {steps.map((row) => (
                  <tr key={row.label} className="border-b border-gray-100">
                    <td className="py-2.5 pr-3 font-medium text-gray-900">
                      {row.label}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700">
                      {formatNumber(row.users)}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700">
                      {formatPercent(row.conversion)}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700">
                      {row.dropOff > 0 ? formatPercent(row.dropOff) : "—"}
                    </td>
                    <td className="py-2.5">
                      <div className="w-28 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-2 rounded-full ${getBarColor(row.conversion)}`}
                          style={{ width: `${row.progressPercent}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {funnel.id === "mentorship" && pipelineStages.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              CRM Pipeline Distribution
            </p>
            <div className="flex flex-wrap gap-2">
              {pipelineStages.map((stage) => (
                <span
                  key={`${stage.stageName}-${stage.position}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-xs text-violet-800"
                >
                  {stage.stageName}
                  <span className="font-semibold">
                    {formatNumber(stage.count)}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
